import { useState, useEffect, useCallback, useRef } from 'react';
import { hapticFeedback } from './useHapticFeedback';

interface PullToRefreshOptions {
  onRefresh: () => Promise<void> | void;
  threshold?: number; // Pull distance to trigger refresh (default: 80)
  maxPull?: number; // Maximum pull distance (default: 150)
  resistance?: number; // Pull resistance factor (default: 2.5)
  disabled?: boolean;
}

interface PullToRefreshState {
  isPulling: boolean;
  isRefreshing: boolean;
  pullDistance: number;
  canRefresh: boolean;
}

export const usePullToRefresh = ({
  onRefresh,
  threshold = 80,
  maxPull = 150,
  resistance = 2.5,
  disabled = false,
}: PullToRefreshOptions) => {
  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    isRefreshing: false,
    pullDistance: 0,
    canRefresh: false,
  });

  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const hasTriggeredHaptic = useRef<boolean>(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (disabled || state.isRefreshing) return;
    
    // Only enable pull-to-refresh when at top of page
    if (window.scrollY > 5) return;
    
    startY.current = e.touches[0].clientY;
    hasTriggeredHaptic.current = false;
    setState(prev => ({ ...prev, isPulling: true }));
  }, [disabled, state.isRefreshing]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!state.isPulling || disabled || state.isRefreshing) return;
    if (window.scrollY > 5) {
      setState(prev => ({ ...prev, isPulling: false, pullDistance: 0 }));
      return;
    }

    currentY.current = e.touches[0].clientY;
    const deltaY = currentY.current - startY.current;

    if (deltaY > 0) {
      // Apply resistance to the pull
      const pullDistance = Math.min(deltaY / resistance, maxPull);
      const canRefresh = pullDistance >= threshold;

      // Trigger haptic when crossing threshold
      if (canRefresh && !hasTriggeredHaptic.current) {
        hapticFeedback('medium');
        hasTriggeredHaptic.current = true;
      } else if (!canRefresh && hasTriggeredHaptic.current) {
        hasTriggeredHaptic.current = false;
      }

      setState(prev => ({
        ...prev,
        pullDistance,
        canRefresh,
      }));

      // Prevent default scrolling when pulling
      if (pullDistance > 10) {
        e.preventDefault();
      }
    }
  }, [state.isPulling, disabled, state.isRefreshing, resistance, maxPull, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (!state.isPulling) return;

    if (state.canRefresh && !state.isRefreshing) {
      setState(prev => ({ ...prev, isRefreshing: true, pullDistance: threshold }));
      hapticFeedback('success');

      try {
        await onRefresh();
      } catch (error) {
        console.error('Refresh failed:', error);
        hapticFeedback('error');
      } finally {
        setState({
          isPulling: false,
          isRefreshing: false,
          pullDistance: 0,
          canRefresh: false,
        });
      }
    } else {
      setState({
        isPulling: false,
        isRefreshing: false,
        pullDistance: 0,
        canRefresh: false,
      });
    }
  }, [state.isPulling, state.canRefresh, state.isRefreshing, threshold, onRefresh]);

  useEffect(() => {
    if (disabled) return;

    const options: AddEventListenerOptions = { passive: false };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, options);
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, disabled]);

  // Calculate progress percentage (0-1)
  const progress = Math.min(state.pullDistance / threshold, 1);

  return {
    ...state,
    progress,
    threshold,
  };
};
